import React from 'react';
import { Chip } from '@mui/material';
import { Close, CalendarToday, Category, QrCode, PriorityHigh, Warning, Flag, Person, Engineering } from '@mui/icons-material';
import type { DateRangeValue } from '../DateRangeFilter';

const chipSx = {
  height: 24,
  backgroundColor: '#21316a',
  color: 'white',
  '& .MuiChip-label': { color: 'white' },
  '& .MuiChip-icon': { color: 'white !important' },
  '& .MuiChip-deleteIcon': { color: 'white !important' },
};

interface NamedOption {
  key: string;
  name: string;
}

interface ActiveFilterChipsProps {
  selectedClients: string[];
  onClearClients: () => void;
  dateRange: DateRangeValue;
  pageType: 'demands' | 'reports';
  onClearDateRange: () => void;
  statusOptions: NamedOption[];
  selectedStatuses: string[];
  onSetSelectedStatuses: (values: string[]) => void;
  categories: NamedOption[];
  selectedCategories: string[];
  onSetSelectedCategories: (values: string[]) => void;
  priorities: NamedOption[];
  selectedPriorities: string[];
  onSetSelectedPriorities: (values: string[]) => void;
  selectedQrCodeIdentifiers: string[];
  onSetSelectedQrCodeIdentifiers: (values: string[]) => void;
  /** Item 10: filtro por responsável e por prestador — opcionais (páginas sem esse conceito,
   * como Sugestões, simplesmente não passam esses props). */
  assigneeOptions?: NamedOption[];
  selectedAssigneeIds?: string[];
  onSetSelectedAssigneeIds?: (values: string[]) => void;
  providerOptions?: NamedOption[];
  selectedProviderIds?: string[];
  onSetSelectedProviderIds?: (values: string[]) => void;
  showSimilarOnly: boolean;
  onClearSimilarOnly: () => void;
}

/**
 * Chips dos filtros ativos (condomínios, período, situação, categoria, prioridade, QR codes,
 * "apenas similares"), cada um removível individualmente. Extraído de AdvancedFilters.tsx (F7).
 */
export const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  selectedClients,
  onClearClients,
  dateRange,
  onClearDateRange,
  statusOptions,
  selectedStatuses,
  onSetSelectedStatuses,
  categories,
  selectedCategories,
  onSetSelectedCategories,
  priorities,
  selectedPriorities,
  onSetSelectedPriorities,
  selectedQrCodeIdentifiers,
  onSetSelectedQrCodeIdentifiers,
  assigneeOptions = [],
  selectedAssigneeIds = [],
  onSetSelectedAssigneeIds,
  providerOptions = [],
  selectedProviderIds = [],
  onSetSelectedProviderIds,
  showSimilarOnly,
  onClearSimilarOnly,
}) => {
  const chips: React.ReactNode[] = [];

  if (selectedClients.length > 0) {
    chips.push(
      <Chip
        key="clients"
        label={`${selectedClients.length} condomínio${selectedClients.length > 1 ? 's' : ''}`}
        size="small"
        onDelete={onClearClients}
        deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
        sx={chipSx}
      />
    );
  }

  if (dateRange.type !== 'all') {
    chips.push(
      <Chip
        key="date"
        label={dateRange.label}
        size="small"
        onDelete={onClearDateRange}
        deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
        icon={<CalendarToday sx={{ fontSize: 14, color: 'white !important' }} />}
        sx={chipSx}
      />
    );
  }

  (selectedStatuses || []).forEach((key) => {
    const s = statusOptions.find((x) => x.key === key);
    chips.push(
      <Chip
        key={`status-${key}`}
        label={s?.name || key}
        size="small"
        onDelete={() => onSetSelectedStatuses(selectedStatuses.filter((x) => x !== key))}
        deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
        icon={<Flag sx={{ fontSize: 14, color: 'white !important' }} />}
        sx={chipSx}
      />
    );
  });

  (selectedCategories || []).forEach((key) => {
    const category = categories.find((c) => c.key === key);
    chips.push(
      <Chip
        key={`category-${key}`}
        label={category?.name || key}
        size="small"
        onDelete={() => onSetSelectedCategories(selectedCategories.filter((x) => x !== key))}
        deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
        icon={<Category sx={{ fontSize: 14, color: 'white !important' }} />}
        sx={chipSx}
      />
    );
  });

  (selectedPriorities || []).forEach((key) => {
    const priority = priorities.find((p) => p.key === key);
    chips.push(
      <Chip
        key={`priority-${key}`}
        label={priority?.name || key}
        size="small"
        onDelete={() => onSetSelectedPriorities(selectedPriorities.filter((x) => x !== key))}
        deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
        icon={<PriorityHigh sx={{ fontSize: 14, color: 'white !important' }} />}
        sx={chipSx}
      />
    );
  });

  (selectedQrCodeIdentifiers || []).forEach((identifier) => {
    chips.push(
      <Chip
        key={`qrcode-${identifier}`}
        label={identifier}
        size="small"
        onDelete={() => onSetSelectedQrCodeIdentifiers(selectedQrCodeIdentifiers.filter((x) => x !== identifier))}
        deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
        icon={<QrCode sx={{ fontSize: 14, color: 'white !important' }} />}
        sx={chipSx}
      />
    );
  });

  (selectedAssigneeIds || []).forEach((key) => {
    const user = assigneeOptions.find((u) => u.key === key);
    chips.push(
      <Chip
        key={`assignee-${key}`}
        label={user?.name || key}
        size="small"
        onDelete={() => onSetSelectedAssigneeIds?.(selectedAssigneeIds.filter((x) => x !== key))}
        deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
        icon={<Person sx={{ fontSize: 14, color: 'white !important' }} />}
        sx={chipSx}
      />
    );
  });

  (selectedProviderIds || []).forEach((key) => {
    const provider = providerOptions.find((p) => p.key === key);
    chips.push(
      <Chip
        key={`provider-${key}`}
        label={provider?.name || key}
        size="small"
        onDelete={() => onSetSelectedProviderIds?.(selectedProviderIds.filter((x) => x !== key))}
        deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
        icon={<Engineering sx={{ fontSize: 14, color: 'white !important' }} />}
        sx={chipSx}
      />
    );
  });

  if (showSimilarOnly) {
    chips.push(
      <Chip
        key="similar"
        label="Apenas similares"
        size="small"
        onDelete={onClearSimilarOnly}
        deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
        icon={<Warning sx={{ fontSize: 14, color: 'white !important' }} />}
        sx={chipSx}
      />
    );
  }

  return <>{chips}</>;
};

export default ActiveFilterChips;
